import { useContext, useMemo } from 'react';
import { ForecastContext } from '../../../contexts/investment-robot/ForecastContext';
import { IconElements } from '../../../config/icons';
import { useTranslation } from 'react-i18next';
import { toPrice } from '../../../utils/parsers';

export const ForecastResultsSticky: React.FC<{}> = ({ children }) => {
    const forecastContext = useContext(ForecastContext);
    const forecast = forecastContext.forecast;
    const { t } = useTranslation('investment');

    const lastForecastValue = forecastContext.forecast.query.data?.forecastExpirationValues;

    const reachesGoal =
        lastForecastValue?.median && forecastContext.forecast.parameters.capitalGoal.amount <= lastForecastValue.median;

    const suggestions = useMemo(() => {
        const suggestedInitial =
            forecast.parameters.deposits.initial.amount + (forecast.query.data?.suggest?.paymentSingleAmount || 0);
        const suggestedPeriodicYearly =
            forecast.parameters.deposits.periodic.amount *
            (forecast.parameters.deposits.periodic.interval === 'monthly' ? 12 : 1) +
            (forecast.query.data?.suggest?.paymentRecurringAmountYearly ||
                forecast.query.data?.suggest?.paymentRecurringAmount ||
                0);

        return {
            initial: suggestedInitial,
            periodicYearly: suggestedPeriodicYearly,
        };
    }, [forecast.query.data]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            {forecastContext.forecast.parameters.capitalGoal.amount > 0 && reachesGoal && (
                <div className="flex flex-row items-center justify-center">
                    <IconElements.Check className="ml-2 w-6 h-6 mr-2 text-bnd-green" />
                    <p className={`p-2 font-semibold text-sm text-bnd-green`}>
                        {t('forecast-screen-result-description-positive')}
                    </p>
                </div>
            )}
            {forecastContext.forecast.parameters.capitalGoal.amount > 0 && !reachesGoal && (
                <div className="flex flex-row mb-8">
                    <IconElements.Cross className="w-6 h-6 mt-4 mr-2 text-bnd-orange" />

                    <div className="flex-1 flex flex-col">
                        <p className={`p-2 font-semibold text-sm text-bnd-orange`}>
                            {t('forecast-screen-result-description-negative')}
                        </p>
                        <ul className="mt-2 list-none text-sm text-bnd-orange">
                            <li>
                                -{' '}
                                {forecast.parameters.deposits.periodic.interval === 'monthly'
                                    ? t('forecast-screen-calculation-suggestions-periodic-month')
                                    : t('forecast-screen-calculation-suggestions-periodic-year')}{' '}
                                <span className="font-bold">
                                    {toPrice(
                                        suggestions.periodicYearly /
                                        (forecast.parameters.deposits.periodic.interval === 'monthly' ? 12 : 1),
                                        { onlyFullEuros: true, withCurrencySymbol: true },
                                    )}
                                </span>
                            </li>
                            <li>
                                - {t('forecast-screen-calculation-suggestions-initial')}{' '}
                                <span className="font-bold">
                                    {toPrice(suggestions.initial, {
                                        onlyFullEuros: true,
                                        withCurrencySymbol: true,
                                    })}{' '}
                                </span>
                            </li>
                            <li>- {t('forecast-screen-calculation-suggestions-duration')}</li>
                            <li>- {t('forecast-screen-calculation-suggestions-strategy')}</li>
                            <li>- {t('forecast-screen-calculation-suggestions-capital-goal')}</li>
                        </ul>
                    </div>
                </div>
            )}
        </>
    );
};
